import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { verifyRecaptcha } from "@/lib/recaptcha.server";

const ContactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(5000),
  recaptcha_token: z.string().optional(),
});

function getClientIp(): string | null {
  const xff = getRequestHeader("x-forwarded-for");
  if (xff) return xff.split(",")[0]?.trim() ?? null;
  const real = getRequestHeader("x-real-ip");
  return real ?? null;
}

/**
 * Submit contact form (anonymous or authenticated).
 * - Verifies reCAPTCHA v3 token (when RECAPTCHA_V3_SECRET_KEY is set).
 * - Saves to `contacts` table.
 * - TODO(EE_02): admin にメール通知を送る（メール基盤整備後）。
 */
export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ContactSchema.parse(input))
  .handler(async ({ data }) => {
    const verdict = await verifyRecaptcha(data.recaptcha_token, "contact_submit");
    if (!verdict.ok) {
      console.warn("[contact] recaptcha rejected:", verdict.reason, verdict.score);
      throw new Error("RECAPTCHA_FAILED");
    }

    const ua = getRequestHeader("user-agent")?.slice(0, 500) ?? null;
    const ip = getClientIp();

    let userId: string | null = null;
    const auth = getRequestHeader("authorization");
    if (auth?.startsWith("Bearer ")) {
      try {
        const token = auth.slice(7);
        const { data: claims } = await supabaseAdmin.auth.getUser(token);
        userId = claims.user?.id ?? null;
      } catch {
        userId = null;
      }
    }

    const { error } = await supabaseAdmin.from("contacts").insert({
      user_id: userId,
      name: data.name,
      email: data.email,
      subject: data.subject,
      body: data.body,
      ip,
      ua,
    });
    if (error) throw new Error(error.message);

    // eslint-disable-next-line no-console
    console.log("[contact] new submission", { email: data.email, subject: data.subject });
    return { ok: true };
  });

export interface ContactRow {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  subject: string;
  body: string;
  ip: string | null;
  ua: string | null;
  created_at: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function ensureAdmin(supabase: any, userId: string): Promise<void> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("FORBIDDEN");
}

export const listContacts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ContactRow[]> => {
    const { supabase, userId } = context;
    await ensureAdmin(supabase, userId);
    const { data, error } = await supabaseAdmin
      .from("contacts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []) as ContactRow[];
  });
